import type { Budget, Choice, Model, Priority, Recommendation, Scene, ScoreKey } from './types'

const weights: Record<Scene, Partial<Record<ScoreKey, number>>> = {
  code: { coding: .3, agent: .2, reasoning: .15, stability: .15, speed: .1 },
  writing: { writing: .3, reasoning: .2, longContext: .15, stability: .15, speed: .1 },
  vision: { vision: .35, reasoning: .15, coding: .15, stability: .15, speed: .1 },
  report: { longContext: .25, writing: .25, reasoning: .2, stability: .15, agent: .05 },
  batch: { speed: .25, stability: .15, agent: .1, coding: .1, reasoning: .05 },
  reasoning: { reasoning: .3, coding: .2, agent: .2, stability: .15, longContext: .1 },
  realtime: { reasoning: .2, speed: .2, stability: .2, writing: .1 },
}

const numericContext = (value: string) => value.includes('1M') ? 1000 : value.includes('500K') ? 500 : value.includes('256K') ? 256 : 0
export const hasVision = (m: Model) => m.scores.vision > 0 && (Array.isArray(m.specs.modalities) ? m.specs.modalities.join(' ') : m.specs.modalities).includes('图片')
export const typicalCost = (m: Model) => {
  const p = m.price.cny
  if (p.input == null || p.output == null) return null
  return p.input * .75 + p.output * .25
}
export const priceLabel = (m: Model) => {
  const cost = typicalCost(m)
  if (cost == null) return m.channel === 'official-subscription' ? '订阅计费，不可直接比较' : '动态／倍率计费'
  return `约 ¥${cost.toFixed(cost < 10 ? 1 : 0)}/1M（3:1）`
}
const priceScore = (m: Model, candidates: Model[]) => {
  const costs = candidates.map(typicalCost).filter((x): x is number => x !== null)
  const current = typicalCost(m)
  if (current === null || costs.length < 2) return 0
  const min = Math.min(...costs), max = Math.max(...costs)
  if (min === max) return 6
  return 10 * (1 - (Math.log(current) - Math.log(min)) / (Math.log(max) - Math.log(min)))
}
export function recommend(models: Model[], choice: Choice): Recommendation {
  let candidates = models.filter(m => choice.relay || m.channel !== 'relay')
  if (choice.channel !== 'any') candidates = candidates.filter(m => m.channel === choice.channel)
  if (choice.vision) candidates = candidates.filter(hasVision)
  if (choice.context !== 'any') candidates = candidates.filter(m => numericContext(m.specs.contextWindow) >= Number.parseInt(choice.context))
  if (choice.scene === 'realtime') candidates = candidates.filter(m => m.id === 'grok-4.5' || m.id === 'openai-gpt-5.6-sol')
  // Subscription, dynamic and multiplier pricing are intentionally not treated as free.
  // They may be explored only when the user explicitly has no budget constraint.
  if (choice.budget === 'low') candidates = candidates.filter(m => m.flags.free || (typicalCost(m) !== null && typicalCost(m)! <= 12))
  if (choice.budget === 'medium') candidates = candidates.filter(m => m.flags.free || (typicalCost(m) !== null && typicalCost(m)! <= 45))
  const scored = candidates.map(m => {
    const w = { ...weights[choice.scene] }
    let priceWeight = choice.scene === 'batch' ? .35 : choice.scene === 'reasoning' ? .05 : .1
    if (choice.priority === 'price') priceWeight = Math.max(.35, priceWeight)
    if (choice.priority === 'speed') w.speed = Math.max(.25, w.speed || 0)
    if (choice.priority === 'stability') w.stability = Math.max(.3, w.stability || 0)
    if (choice.priority === 'quality') { const core = Object.keys(w)[0] as ScoreKey; w[core] = (w[core] || 0) + .08; priceWeight = Math.max(.03, priceWeight - .05) }
    const ability = Object.entries(w).reduce((sum, [key, weight]) => sum + m.scores[key as ScoreKey] * (weight || 0), 0)
    const score = ability + priceScore(m, candidates) * priceWeight + (m.flags.free ? .45 : 0)
    return { m, score }
  }).sort((a, b) => b.score - a.score)
  const primary = scored[0]?.m ?? null
  const alternatives = scored.slice(1).map(x => x.m).filter(m => m.id !== primary?.id).slice(0, 2)
  const core = Object.entries(weights[choice.scene]).sort((a,b) => b[1]! - a[1]!)[0]?.[0] as ScoreKey
  const reasons = primary ? [
    `你选择了${sceneText(choice.scene)}和${priorityText(choice.priority)}；它的${scoreText(core)}为 ${primary.scores[core]}/10。`,
    primary.flags.free ? '当前有免费状态，但请留意它标注的有效期与排队风险。' : `${priceLabel(primary)}，与当前候选按同一官方 API 口径估算。`,
    `${scoreText('stability')} ${primary.scores.stability}/10，适合需要可预期交付的任务。`,
  ] : []
  return { primary, alternatives, score: scored[0]?.score ?? 0, reasons, warning: primary?.weaknesses[0] ?? null, candidates: candidates.length }
}
export function estimateMonthlyCost(m: Model, inputM: number, outputM: number, hitRate: number, factor: number) {
  const p = m.price.cny
  if (p.input == null || p.output == null) return { value: null, fallback: false }
  const cache = p.cacheHit ?? p.input
  return { value: (inputM * (p.input * (1-hitRate) + cache * hitRate) + outputM * p.output) * factor, fallback: p.cacheHit == null }
}
export const sceneText = (s: Scene) => ({ code:'写代码',writing:'写作与分析',vision:'看图与 UI',report:'长报告',batch:'批量处理',reasoning:'疑难攻坚',realtime:'实时信息' })[s]
export const priorityText = (p: Priority) => ({ quality:'效果',speed:'速度',price:'省钱',stability:'稳定' })[p]
export const scoreText = (s: ScoreKey) => ({ coding:'编程能力',agent:'Agent 能力',reasoning:'推理能力',longContext:'长上下文',writing:'写作能力',vision:'视觉能力',speed:'响应速度',stability:'稳定性' })[s]
export const budgetText = (b: Budget) => ({ low:'低预算',medium:'中等预算',any:'不限' })[b]
